import React from 'react';
import { Dimmer, Loader, Message, Table, Button, Icon, Label, Popup, Dropdown, Pagination, Checkbox } from 'semantic-ui-react';
import {
  ContractListDto,
  ContractListPagedResponseDto,
  ContractFiltersDto,
  EContractStatus,
} from '../../services/contracts/ContractService';
import GlassTable from '../Common/GlassTable';
import { getDefaultButtonStyle, getButtonHoverEffects } from '../../styles/designSystem';
import { formatCnpj } from '../../services/utils/stringUtils';
import {
  aggregateGroupContractStatus,
  getContractStatusSemanticColor,
  getContractStatusText as getStatusText,
  getQualityCcvFlag,
  isCarteiraListaStatusChangeBlocked,
  isContractDraftStatus,
  normalizeContractStatus,
} from '../../utils/contractStatusUtils';
import {
  getContractIdsForVtmeEmail,
  getContractTypeLabel,
  sortContractsForVtmeGroupOrder,
} from '../../utils/contractGroupVtmeOrder';
import StatusHistoryPopover from './StatusHistoryPopover';
import DocusignSendHistoryPopover from './DocusignSendHistoryPopover';
import { isMeiOuIndividual } from '../../utils/iePolicy';
import {
  aggregateGroupLatestDocusignSend,
  formatDocusignTooltip,
  getDocusignBadgeConfig,
  resolveLatestDocusignSendFromContract,
} from '../../utils/docusignSendHistoryUtils';
import { resolveFastchipDeliveryBadge } from '../../utils/fastchipDeliveryLabel';
import { LatestDocusignSendSummaryDto } from '../../services/contracts/ContractService';
import { ContractHandsetBadge } from './ContractHandsetBadge';
import ConsultantCreatorCell from './ConsultantCreatorCell';
import { isDocusignUiEnabled } from '../../config/docusignUi';

export interface ContractTableProps {
  contracts: ContractListPagedResponseDto | null;
  contractsLoading: boolean;
  contractsError?: string | null;
  onRetryLoad?: () => void;
  currentPage: number;
  totalPages: number;
  filters: ContractFiltersDto;
  onPageChange: (event: React.MouseEvent<HTMLAnchorElement>, data: { activePage?: string | number }) => void;
  expandedGroups: Set<string>;
  onToggleGroup: (groupId: string) => void;
  user: { roles?: string[] } | null;
  hasRole: (role: string) => boolean;
  onViewLines: (contractId: string, contractInfo: any) => void;
  onOpenTracking: (contractId: string, trackingCode?: string | null) => void;
  onEdit: (contractId: string) => void;
  onContinueDraft?: (contractId: string) => void;
  onDeleteDraft?: (contractId: string) => void;
  onDelete: (contractId: string) => void;
  onStatusChange: (contractId: string, status: EContractStatus) => void;
  onDownloadAll: (contractId: string) => void;
  onDownloadGroup: (groupId: string) => void;
  onDuplicate: (contractId: string) => void;
  onSendToDocusign: (contract: ContractListDto) => void;
  onSendToDocusignFromMenu?: (contract: ContractListDto) => void;
  /** Um ou mais IDs (grupo misto = Aditivo + Renegociação, na ordem VTME). */
  onSendEmail: (contractIds: string[]) => void;
  onViewEditHistory: (contractId: string) => void;
  onMarkFastchipEnviado?: (contractId: string, contractStatus?: number | EContractStatus | null) => void;
  onCancelPendingSuspensions?: (contractId: string, contractInfo: any) => void;
  isDownloadingContracts: boolean;
  duplicatingContractId: string | null;
  selectedContractIds?: Set<string>;
  onToggleContractSelection?: (contractId: string, checked: boolean) => void;
  onToggleAllContractSelection?: (checked: boolean, visibleIds: string[]) => void;
  showRestoreSelection?: boolean;
}

type DisplayContract = ContractListDto & {
  isGroupParent?: boolean;
  groupContracts?: ContractListDto[];
  clientEstado?: string;
  user?: {
    id: string;
    nome: string;
    email: string;
    username: string;
  };
  administrator?: {
    nomeCompleto?: string;
    cpf?: string;
  };
  representatives?: Array<{
    id: string;
    nome: string;
    cpf?: string;
  }>;
};

/** RF17: Qualidade OK / Aprovado CCV / Recusado CCV só no modal de linhas — nunca neste dropdown. */
function isOmittedFromTableStatusDropdown(status: EContractStatus): boolean {
  return (
    status === EContractStatus.QualidadeOk ||
    status === EContractStatus.AprovadoCCV ||
    status === EContractStatus.RecusadoCCV
  );
}

function renderDocusignBadge(
  contract: ContractListDto & { isGroupParent?: boolean; groupContracts?: ContractListDto[] },
  onSendToDocusign: (contract: ContractListDto) => void
) {
  const badge = getDocusignBadgeConfig(contract);
  // Módulo DocuSign desativado: histórico/badge pode aparecer; sem CTA de envio/reenvio.
  const clickable = isDocusignUiEnabled() && badge.clickable;

  const label = (
    <Label
      size="tiny"
      color={badge.color}
      style={{
        marginTop: '4px',
        cursor: clickable ? 'pointer' : 'default',
      }}
      onClick={
        clickable
          ? (e: React.MouseEvent) => {
              e.stopPropagation();
              onSendToDocusign(contract);
            }
          : undefined
      }
    >
      {badge.text}
    </Label>
  );

  if (badge.showDetailTooltip && badge.detailSummary) {
    return (
      <Popup
        content={<DocusignTooltipContent summary={badge.detailSummary} />}
        trigger={label}
        hoverable
      />
    );
  }

  if (badge.hoverMessage) {
    return (
      <Popup
        content={<div style={{ whiteSpace: 'pre-line' }}>{badge.hoverMessage}</div>}
        trigger={label}
        hoverable
      />
    );
  }

  return label;
}

function DocusignTooltipContent({ summary }: { summary: LatestDocusignSendSummaryDto }) {
  return <div style={{ whiteSpace: 'pre-line' }}>{formatDocusignTooltip(summary)}</div>;
}

const EDITABLE_STATUSES: EContractStatus[] = [
  EContractStatus.Criado,
  EContractStatus.Assinado,
  EContractStatus.AguardandoCorrecaoConsultor,
  EContractStatus.AguardandoAssinatura,
  EContractStatus.NaoAssinado,
  EContractStatus.RecusadoCQV,
];

const isContractEditable = (status?: EContractStatus | string | number | null): boolean => {
  const n = normalizeContractStatus(status);
  if (n === null) return true;
  return EDITABLE_STATUSES.includes(n);
};

export const ContractTable: React.FC<ContractTableProps> = (props) => {
  const {
    contracts,
    contractsLoading,
    contractsError = null,
    onRetryLoad,
    currentPage,
    totalPages,
    filters,
    onPageChange,
    expandedGroups,
    onToggleGroup,
    user,
    hasRole,
    onViewLines,
    onOpenTracking,
    onEdit,
    onContinueDraft,
    onDeleteDraft,
    onDelete,
    onStatusChange,
    onDownloadAll,
    onDownloadGroup,
    onDuplicate,
    onSendToDocusign,
    onSendToDocusignFromMenu,
    onSendEmail,
    onViewEditHistory,
    onMarkFastchipEnviado,
    onCancelPendingSuspensions,
    isDownloadingContracts,
    duplicatingContractId,
    selectedContractIds = new Set<string>(),
    onToggleContractSelection,
    onToggleAllContractSelection,
    showRestoreSelection = false,
  } = props;

  const handleSendToDocusignFromMenu = onSendToDocusignFromMenu ?? onSendToDocusign;

  const hasItems = (contracts?.items?.length ?? 0) > 0;

  if (contractsLoading && !hasItems) {
    return (
      <Dimmer active>
        <Loader size="large">Carregando contratos...</Loader>
      </Dimmer>
    );
  }

  if (contractsError && !hasItems) {
    return (
      <Message negative icon>
        <Icon name="warning circle" />
        <Message.Content>
          <Message.Header>Não foi possível carregar os contratos</Message.Header>
          <p>{contractsError}</p>
          {onRetryLoad && (
            <Button
              primary
              size="small"
              icon="refresh"
              content="Tentar novamente"
              onClick={onRetryLoad}
              style={{ marginTop: '0.75rem' }}
            />
          )}
        </Message.Content>
      </Message>
    );
  }

  if (!contractsLoading && !contractsError && !hasItems) {
    return (
      <Message info>
        <Message.Header>Nenhum contrato encontrado</Message.Header>
        <p>Não foram encontrados contratos com os filtros aplicados.</p>
      </Message>
    );
  }

  const staleDataBanner =
    contractsError && hasItems ? (
      <Message warning icon style={{ marginBottom: '1rem' }}>
        <Icon name="warning sign" />
        <Message.Content>
          <Message.Header>Não foi possível atualizar a lista</Message.Header>
          <p>{contractsError}</p>
          {onRetryLoad && (
            <Button
              size="small"
              icon="refresh"
              content="Tentar novamente"
              onClick={onRetryLoad}
              style={{ marginTop: '0.5rem' }}
            />
          )}
        </Message.Content>
      </Message>
    ) : null;

  const groupedContractsMap = new Map<string, ContractListDto[]>();
  const individualContracts: ContractListDto[] = [];

  contracts!.items.forEach((contract) => {
    if (contract.groupId) {
      if (!groupedContractsMap.has(contract.groupId)) {
        groupedContractsMap.set(contract.groupId, []);
      }
      groupedContractsMap.get(contract.groupId)!.push(contract);
    } else {
      individualContracts.push(contract);
    }
  });

  const displayContracts: DisplayContract[] = [];

  groupedContractsMap.forEach((groupContracts) => {
    const sortedGroupContracts = sortContractsForVtmeGroupOrder(groupContracts);

    if (sortedGroupContracts.length === 1) {
      displayContracts.push(...sortedGroupContracts);
      return;
    }

    const first = sortedGroupContracts[0];
    const statusEfetivo =
      aggregateGroupContractStatus(sortedGroupContracts) ?? EContractStatus.Criado;
    const vtmeConfirmado =
      sortedGroupContracts.some((c: any) => (c as any).vtmeConfirmado === true) ||
      statusEfetivo === EContractStatus.VtmeCriado;
    const parentContract: DisplayContract = {
      ...first,
      isGroupParent: true,
      groupContracts: sortedGroupContracts,
      status: statusEfetivo,
      valorTotalMensal: sortedGroupContracts.reduce((sum, c) => sum + (c.valorTotalMensal || 0), 0),
      totalLinhas: sortedGroupContracts.reduce((sum, c) => sum + (c.totalLinhas || 0), 0),
      temPortabilidade: sortedGroupContracts.some((c) => (c as any).temPortabilidade),
      temTrocaLinha: sortedGroupContracts.some((c) => c.temTrocaLinha),
      temAparelhoCatalogo: sortedGroupContracts.some((c) => c.temAparelhoCatalogo),
      temEsim: sortedGroupContracts.some((c) => c.temEsim),
      latestDocusignSend: aggregateGroupLatestDocusignSend(sortedGroupContracts),
    };
    (parentContract as any).vtmeConfirmado = vtmeConfirmado;
    displayContracts.push(parentContract);
  });

  displayContracts.push(...individualContracts);
  displayContracts.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

  const visibleContractIds = displayContracts.flatMap((contract) => {
    if (contract.isGroupParent && contract.groupContracts) {
      return contract.groupContracts
        .filter((sub) => normalizeContractStatus(sub.status) === EContractStatus.Cancelado)
        .map((sub) => sub.id);
    }
    if (normalizeContractStatus(contract.status) === EContractStatus.Cancelado) {
      return [contract.id];
    }
    return [];
  });

  const allVisibleSelected =
    visibleContractIds.length > 0 &&
    visibleContractIds.every((id) => selectedContractIds.has(id));

  const renderRestoreCheckbox = (contractId: string, status: EContractStatus | string | number | null | undefined) => {
    if (!showRestoreSelection || normalizeContractStatus(status) !== EContractStatus.Cancelado) {
      return null;
    }
    return (
      <Checkbox
        checked={selectedContractIds.has(contractId)}
        onChange={(_, data) =>
          onToggleContractSelection?.(contractId, Boolean(data.checked))
        }
      />
    );
  };

  const renderGroupRestoreCheckbox = (contract: DisplayContract) => {
    if (!showRestoreSelection || !contract.isGroupParent || !contract.groupContracts?.length) {
      return null;
    }

    const cancelledChildIds = contract.groupContracts
      .filter((sub: ContractListDto) => normalizeContractStatus(sub.status) === EContractStatus.Cancelado)
      .map((sub: ContractListDto) => sub.id);

    if (cancelledChildIds.length === 0) {
      return null;
    }

    const allSelected = cancelledChildIds.every((id: string) => selectedContractIds.has(id));
    const someSelected = cancelledChildIds.some((id: string) => selectedContractIds.has(id));

    return (
      <Checkbox
        checked={allSelected}
        indeterminate={!allSelected && someSelected}
        onChange={(_, data) =>
          onToggleAllContractSelection?.(Boolean(data.checked), cancelledChildIds)
        }
      />
    );
  };

  return (
    <>
      {staleDataBanner}
      <div style={{ position: 'relative', overflow: 'visible', zIndex: 1 }}>
        {contractsLoading && (
          <Dimmer active inverted>
            <Loader size="large">Atualizando contratos...</Loader>
          </Dimmer>
        )}
      <style>{`
        .status-dropdown-menu {
          z-index: 100000 !important;
          max-height: 400px !important;
          overflow-y: auto !important;
          overflow-x: hidden !important;
        }
        .ui.dropdown .menu {
          z-index: 100000 !important;
        }
        .ui.dropdown.active .menu.visible {
          z-index: 100000 !important;
        }
        .ui.dropdown.upward .menu {
          bottom: 100% !important;
          top: auto !important;
        }
        .ui.dropdown.active .menu {
          position: absolute !important;
          z-index: 100000 !important;
        }
        .contracts-table-wrapper {
          overflow: visible !important;
        }
        .contracts-table-wrapper > div {
          overflow: visible !important;
        }
        .contracts-table-wrapper > div > div {
          overflow: visible !important;
        }
        .ui.dropdown.button.status-dropdown {
          z-index: 10 !important;
        }
        .ui.dropdown.button.status-dropdown.active {
          z-index: 100001 !important;
        }
        .ui.dropdown.button.status-dropdown.active .menu {
          z-index: 100002 !important;
        }
        .ui.dropdown.button.status-dropdown:not(.active) {
          z-index: 10 !important;
        }
        .contract-more-actions-dropdown.active {
          z-index: 100001 !important;
        }
        .contract-more-actions-dropdown.active .menu {
          z-index: 100002 !important;
        }
        @media (max-width: 1200px) {
          .contracts-table-responsive {
            overflow-x: auto !important;
            -webkit-overflow-scrolling: touch;
          }
          .contracts-table-responsive table {
            min-width: 1000px;
          }
          .contract-cell-hide-md {
            display: none !important;
          }
          .contract-actions-mobile {
            flex-direction: column !important;
            gap: 4px !important;
          }
          .contract-actions-mobile .ui.button {
            width: 100% !important;
            margin: 0 !important;
          }
        }
        @media (max-width: 768px) {
          .contracts-table-responsive {
            overflow-x: auto !important;
            -webkit-overflow-scrolling: touch;
          }
          .contracts-table-responsive table {
            min-width: 1200px;
          }
          .contract-cell-hide-sm {
            display: none !important;
          }
          .contract-cell-hide-md {
            display: none !important;
          }
          .contract-header-mobile {
            flex-direction: column !important;
            align-items: flex-start !important;
            gap: 10px !important;
          }
          .contract-header-mobile > div:last-child {
            width: 100%;
          }
        }
        @media (max-width: 480px) {
          .contracts-table-responsive {
            overflow-x: auto !important;
            -webkit-overflow-scrolling: touch;
          }
          .contracts-table-responsive table {
            min-width: 1400px;
          }
          .contract-cell-hide-xs {
            display: none !important;
          }
          .contract-cell-hide-sm {
            display: none !important;
          }
          .contract-cell-hide-md {
            display: none !important;
          }
          .contract-actions-mobile .ui.button {
            font-size: 0.75rem !important;
            padding: 0.3rem 0.5rem !important;
          }
        }
      `}</style>
      <div className="contracts-table-wrapper contracts-table-responsive" style={{ position: 'relative', zIndex: 1 }}>
        <div style={{ position: 'relative' }}>
          <GlassTable celled selectable>
            <Table.Header>
              <Table.Row>
                {showRestoreSelection && (
                  <Table.HeaderCell collapsing textAlign="center">
                    <Popup
                      content="Selecione contratos Cancelados para restaurar"
                      trigger={<span>Restaurar</span>}
                    />
                    <div style={{ marginTop: '6px' }}>
                      <Checkbox
                        checked={allVisibleSelected}
                        indeterminate={
                          !allVisibleSelected &&
                          visibleContractIds.some((id) => selectedContractIds.has(id))
                        }
                        disabled={visibleContractIds.length === 0}
                        onChange={(_, data) =>
                          onToggleAllContractSelection?.(Boolean(data.checked), visibleContractIds)
                        }
                      />
                    </div>
                  </Table.HeaderCell>
                )}
                <Table.HeaderCell>Cliente</Table.HeaderCell>
                <Table.HeaderCell>Total Linhas</Table.HeaderCell>
                <Table.HeaderCell>Tipo</Table.HeaderCell>
                <Table.HeaderCell>Valor Mensal</Table.HeaderCell>
                <Table.HeaderCell className="contract-cell-hide-md">Data do Contrato</Table.HeaderCell>
                <Table.HeaderCell className="contract-cell-hide-sm">
                  <div style={{ lineHeight: 1.25 }}>Consultor Responsável</div>
                  <div style={{ fontSize: '0.85em', fontWeight: 'normal', color: '#666' }}>/ Criado por</div>
                </Table.HeaderCell>
                <Table.HeaderCell className="contract-cell-hide-sm">
                  <div style={{ lineHeight: 1.25 }}>CQV / VTME</div>
                </Table.HeaderCell>
                <Table.HeaderCell>Status</Table.HeaderCell>
                <Table.HeaderCell textAlign="center">Ações</Table.HeaderCell>
              </Table.Row>
            </Table.Header>

            <Table.Body>
              {displayContracts.map((contract, index) => {
                const st = normalizeContractStatus(contract.status);
                const isDraft = isContractDraftStatus(contract.status);
                const isSeller = user?.roles && user.roles.includes('Seller');
                const isTeamLeader = user?.roles && user.roles.includes('TeamLeader');
                const isRestrictedRole = isSeller || isTeamLeader;
                const canOnlyChangeToLimitedStatuses = isSeller || isTeamLeader;
                const contractIsAdvanced =
                  contract.status !== undefined && contract.status !== null && !isContractEditable(contract.status);
                const shouldHideEditableStatuses = isRestrictedRole && contractIsAdvanced;
                const isFirstItem = index === 0;

                return (
                  <React.Fragment key={contract.id}>
                    <Table.Row>
                      {showRestoreSelection && (
                        <Table.Cell collapsing textAlign="center">
                          {contract.isGroupParent
                            ? renderGroupRestoreCheckbox(contract)
                            : renderRestoreCheckbox(contract.id, contract.status)}
                        </Table.Cell>
                      )}
                      <Table.Cell>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          {contract.isGroupParent && contract.groupId && (
                            <Icon
                              name={expandedGroups.has(contract.groupId) ? 'caret down' : 'caret right'}
                              style={{ cursor: 'pointer', color: '#3498db', fontSize: '1.2em' }}
                              onClick={() => onToggleGroup(contract.groupId!)}
                            />
                          )}
                          <div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                              <strong>
                                {contract.clientRazaoSocial || 'Cliente não informado'}
                                {contract.clientEstado && ` - ${contract.clientEstado}`}
                              </strong>
                              {contract.token?.trim() && (
                                <Label color="brown" size="mini" style={{ cursor: 'default' }}>
                                  Token: {contract.token.trim()}
                                </Label>
                              )}
                              {contract.fastchip && !contract.fastchipEnviadoEm && (() => {
                                const badge = resolveFastchipDeliveryBadge({
                                  fastchip: true,
                                  hasEsim: contract.temEsim === true,
                                });
                                return (
                                <Popup
                                  content={badge.popupContent}
                                  trigger={
                                    <Label color={badge.color} size="mini" style={{ cursor: 'default' }}>
                                      <Icon name={badge.icon} />
                                      {badge.label}
                                    </Label>
                                  }
                                />
                                );
                              })()}
                              {contract.fastchip && contract.fastchipEnviadoEm && (
                                <Popup
                                  content={`Fast Chip enviado em ${new Date(contract.fastchipEnviadoEm).toLocaleString('pt-BR')}`}
                                  trigger={
                                    <Label color="teal" size="mini" style={{ cursor: 'default' }}>
                                      <Icon name="shipping fast" />
                                      FC Enviado
                                    </Label>
                                  }
                                />
                              )}
                            </div>
                            {(contract.clientNomeFantasia || contract.clientCnpj) && (
                              <div style={{ fontSize: '0.9em', color: '#666' }}>
                                {contract.clientNomeFantasia && contract.clientNomeFantasia.trim()
                                  ? `${contract.clientNomeFantasia} - ${formatCnpj(contract.clientCnpj)}`
                                  : formatCnpj(contract.clientCnpj)}
                              </div>
                            )}
                            {contract.isGroupParent && (
                              <Label size="tiny" color="blue" style={{ marginTop: '4px' }}>
                                <Icon name="folder open" />
                                Contratos Agrupados ({contract.groupContracts?.length || 0})
                              </Label>
                            )}
                            {contract.temPortabilidade && (
                              <Label size="tiny" color="orange" style={{ marginTop: '4px' }}>
                                <Icon name="exchange" />
                                Portabilidade
                              </Label>
                            )}
                            {contract.temTrocaLinha && (
                              <Label size="tiny" color="orange" style={{ marginTop: '4px' }}>
                                <Icon name="random" />
                                Troca de Linha
                              </Label>
                            )}
                            <ContractHandsetBadge contract={contract} />
                            {/* Badge IE INAPTA — visibilidade interna independente de bypass */}
                            {!contract.ieBypassedAtCreation &&
                              !isMeiOuIndividual({ naturezaJuridica: contract.clientNaturezaJuridica }) &&
                              contract.clientHasActiveInscricaoEstadual === false && (
                                <Popup
                                  content="Cliente sem Inscrição Estadual ativa no UF. Contratos Fastchip são permitidos; o bloqueio de IE inapta aplica-se apenas a entrega TIM (sem Fastchip) com Aditivo."
                                  trigger={
                                    <Label size="tiny" color="red" style={{ marginTop: '4px', cursor: 'default' }}>
                                      <Icon name="warning sign" />
                                      IE INAPTA
                                    </Label>
                                  }
                                />
                              )}
                            {/* Badge BYPASS IE — quando o contrato foi criado com bypass explícito */}
                            {contract.ieBypassedAtCreation && (
                              <Popup
                                content="Este contrato foi criado com bypass da validação de IE inapta. Risco de recusa pela TIM."
                                trigger={
                                  <Label size="tiny" color="orange" style={{ marginTop: '4px', cursor: 'default' }}>
                                    <Icon name="warning circle" />
                                    BYPASS IE
                                  </Label>
                                }
                              />
                            )}
                            {!isDraft && renderDocusignBadge(contract, onSendToDocusign)}
                            {!isDraft && (contract.trackingCode ? (
                              <Popup
                                content={contract.trackingCode}
                                trigger={
                                  <Label
                                    size="tiny"
                                    color="teal"
                                    style={{
                                      marginTop: '4px',
                                      cursor: 'pointer',
                                      maxWidth: '150px',
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap',
                                    }}
                                    onClick={() => onOpenTracking(contract.id, contract.trackingCode)}
                                  >
                                    <Icon name="truck" />
                                    {contract.trackingCode.length > 12
                                      ? `${contract.trackingCode.substring(0, 12)}...`
                                      : contract.trackingCode}
                                  </Label>
                                }
                              />
                            ) : (
                              hasRole('Admin') && (
                                <Popup
                                  content="Cadastrar ou consultar rastreamento (Correios)"
                                  trigger={
                                    <Label
                                      size="tiny"
                                      color="grey"
                                      style={{ marginTop: '4px', cursor: 'pointer' }}
                                      onClick={() => onOpenTracking(contract.id, '')}
                                    >
                                      <Icon name="truck" />
                                      Rastreio
                                    </Label>
                                  }
                                />
                              )
                            ))}
                          </div>
                        </div>
                      </Table.Cell>
                      <Table.Cell>
                        <div>
                          <Label
                            color="blue"
                            size="small"
                            style={{ cursor: 'pointer', marginBottom: '4px', display: 'inline-block' }}
                            onClick={() =>
                              isDraft
                                ? onContinueDraft?.(contract.id)
                                : onViewLines(contract.id, contract)
                            }
                            title={
                              isDraft
                                ? 'Clique para continuar a criação'
                                : 'Clique para ver detalhes das linhas'
                            }
                          >
                            <Icon name="list" />
                            {contract.totalLinhas || 0} {(contract.totalLinhas || 0) === 1 ? 'linha' : 'linhas'}
                          </Label>
                          <Popup
                            trigger={
                              <Icon name="info circle" color="blue" style={{ cursor: 'help', marginLeft: '5px' }} />
                            }
                            content={
                              <div style={{ padding: '5px' }}>
                                <div style={{ marginBottom: '8px' }}>
                                  <strong>Responsável pela Auditoria:</strong>
                                  <div style={{ marginTop: '4px', fontSize: '0.9em' }}>
                                    {contract.administrator?.nomeCompleto || 'Não informado'}
                                  </div>
                                  <div style={{ fontSize: '0.85em', color: '#666', marginTop: '4px', fontStyle: 'italic' }}>
                                    (Pessoa que está na ligação com o vendedor)
                                  </div>
                                </div>
                                {contract.representatives && contract.representatives.length > 0 && (
                                  <div>
                                    <strong>Representantes Legais (QSA):</strong>
                                    <div style={{ marginTop: '4px', fontSize: '0.9em' }}>
                                      {contract.representatives.slice(0, 3).map((rep: any, idx: number) => (
                                        <div key={idx} style={{ marginBottom: '2px' }}>
                                          • {rep.nome || 'Sem nome'}
                                        </div>
                                      ))}
                                      {contract.representatives.length > 3 && (
                                        <div style={{ fontSize: '0.85em', color: '#666', fontStyle: 'italic' }}>
                                          + {contract.representatives.length - 3} mais
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                )}
                              </div>
                            }
                            position="top center"
                            size="small"
                          />
                        </div>
                      </Table.Cell>
                      <Table.Cell>
                        {contract.isGroupParent ? (
                          <Label color="purple">
                            <Icon name="sitemap" />
                            Misto (Aditivo + Renegociação)
                          </Label>
                        ) : isDraft ? (
                          <Label color="grey">
                            <Icon name="edit" style={{ border: 'none', boxShadow: 'none', textShadow: 'none' }} />
                            Rascunho
                          </Label>
                        ) : (
                          <Label color={contract.tipoContratacao === 'Aditivo' ? 'green' : 'blue'}>
                            <Icon name={contract.tipoContratacao === 'Aditivo' ? 'plus circle' : 'refresh'} />
                            {contract.tipoContratacao}
                          </Label>
                        )}
                      </Table.Cell>
                      <Table.Cell>
                        {isDraft && (!contract.valorTotalMensal || contract.valorTotalMensal <= 0) ? (
                          <Label color="grey" size="medium">Rascunho</Label>
                        ) : (
                        <Label color="teal" size="medium">
                          <Icon name="money bill alternate" />
                          R$ {contract.valorTotalMensal?.toFixed(2).replace('.', ',') || '0,00'}
                        </Label>
                        )}
                      </Table.Cell>
                      <Table.Cell className="contract-cell-hide-md">
                        {new Date(contract.createdAt).toLocaleDateString('pt-BR')}
                      </Table.Cell>
                      <Table.Cell className="contract-cell-hide-sm">
                        <ConsultantCreatorCell
                          responsible={contract.user}
                          creator={contract.createdByUser}
                        />
                      </Table.Cell>
                      <Table.Cell className="contract-cell-hide-sm">
                        {contract.vtmeAssignedName ? (
                          <div>
                            <div style={{ fontWeight: 600 }}>{contract.vtmeAssignedName}</div>
                            {contract.vtmeAssignedEmail ? (
                              <div style={{ fontSize: '0.85em', color: '#666' }}>{contract.vtmeAssignedEmail}</div>
                            ) : null}
                          </div>
                        ) : (
                          <span style={{ color: '#999' }}>—</span>
                        )}
                      </Table.Cell>
                      <Table.Cell>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                          {st !== null ? (
                            <>
                              {(() => {
                                const qualityCcvFlag = getQualityCcvFlag(contract.status);
                                return qualityCcvFlag ? (
                                  <Label color={qualityCcvFlag.color} size="small">
                                    <Icon name="flag" />
                                    {qualityCcvFlag.label}
                                  </Label>
                                ) : (
                                  <Label color={getContractStatusSemanticColor(contract.status)}>
                                    {getStatusText(contract.status)}
                                  </Label>
                                );
                              })()}
                              <StatusHistoryPopover
                                contractId={contract.id}
                                currentStatus={st}
                                currentStatusText={getStatusText(contract.status)}
                                updatedAt={contract.updatedAt}
                                historyTitleSuffix={contract.isGroupParent ? ` - ${getContractTypeLabel(contract)}` : undefined}
                                groupContracts={contract.isGroupParent ? contract.groupContracts : undefined}
                              />
                              <DocusignSendHistoryPopover
                                contractId={contract.id}
                                latestSend={resolveLatestDocusignSendFromContract(contract)}
                                groupContracts={contract.isGroupParent ? contract.groupContracts : undefined}
                                historyTitleSuffix={
                                  contract.isGroupParent
                                    ? undefined
                                    : ` - ${getContractTypeLabel(contract)}`
                                }
                              />
                            </>
                          ) : (
                            <Label color="grey">Sem Status</Label>
                          )}
                        </div>
                      </Table.Cell>
                      <Table.Cell textAlign="center">
                        {isDraft ? (
                          <Button.Group size="small" className="contract-actions-mobile">
                            <Popup
                              content="Continuar criação"
                              trigger={
                                <div style={{ display: 'inline-block' }}>
                                  <Button
                                    icon="edit"
                                    title="Continuar criação"
                                    aria-label="Continuar criação"
                                    onClick={() => onContinueDraft?.(contract.id)}
                                    style={{
                                      ...getDefaultButtonStyle(),
                                      padding: '0.4rem 0.8rem',
                                      fontSize: '0.8rem',
                                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                      color: 'white',
                                    }}
                                    {...getButtonHoverEffects('default')}
                                  />
                                </div>
                              }
                            />
                            <Popup
                              content="Excluir rascunho"
                              trigger={
                                <div style={{ display: 'inline-block' }}>
                                  <Button
                                    icon="trash"
                                    title="Excluir rascunho"
                                    aria-label="Excluir rascunho"
                                    onClick={() => onDeleteDraft?.(contract.id)}
                                    style={{
                                      ...getDefaultButtonStyle(),
                                      padding: '0.4rem 0.8rem',
                                      fontSize: '0.8rem',
                                      background: 'linear-gradient(135deg, #e74c3c 0%, #c0392b 100%)',
                                      color: 'white',
                                    }}
                                    {...getButtonHoverEffects('default')}
                                  />
                                </div>
                              }
                            />
                          </Button.Group>
                        ) : (
                        <Button.Group size="small" className="contract-actions-mobile">
                          {hasRole('Admin') && (
                            <Popup
                              content={
                                isContractEditable(contract.status)
                                  ? 'Editar Contrato'
                                  : 'Contrato não pode ser editado neste status'
                              }
                              trigger={
                                <div style={{ display: 'inline-block' }}>
                                  <Button
                                    icon="edit"
                                    title={isContractEditable(contract.status) ? 'Editar Contrato' : 'Contrato não pode ser editado neste status'}
                                    aria-label="Editar contrato"
                                    disabled={!isContractEditable(contract.status)}
                                    onClick={() => onEdit(contract.id)}
                                    style={{
                                      ...getDefaultButtonStyle(),
                                      padding: '0.4rem 0.8rem',
                                      fontSize: '0.8rem',
                                      background: isContractEditable(contract.status)
                                        ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                                        : '#cccccc',
                                      color: 'white',
                                      opacity: isContractEditable(contract.status) ? 1 : 0.6,
                                    }}
                                    {...(isContractEditable(contract.status) ? getButtonHoverEffects('default') : {})}
                                  />
                                </div>
                              }
                            />
                          )}
                          <Popup
                            content={
                              st === EContractStatus.Cancelado
                                ? 'Não é possível baixar contratos cancelados'
                                : contract.isGroupParent
                                ? 'Baixar Todos os PDFs do Grupo (ZIP)'
                                : 'Baixar Todos os Contratos (ZIP)'
                            }
                            trigger={
                              <Button
                                icon="download"
                                title={
                                  contract.isGroupParent
                                    ? 'Baixar Todos os PDFs do Grupo (ZIP)'
                                    : 'Baixar Todos os Contratos (ZIP)'
                                }
                                aria-label="Baixar PDFs"
                                loading={isDownloadingContracts}
                                disabled={isDownloadingContracts || st === EContractStatus.Cancelado}
                                onClick={() =>
                                  contract.isGroupParent && contract.groupId
                                    ? onDownloadGroup(contract.groupId)
                                    : onDownloadAll(contract.id)
                                }
                                style={{
                                  ...getDefaultButtonStyle(),
                                  padding: '0.4rem 0.8rem',
                                  fontSize: '0.8rem',
                                  background: 'linear-gradient(135deg, #27ae60 0%, #2ecc71 100%)',
                                }}
                                {...getButtonHoverEffects('default')}
                              />
                            }
                          />
                          <Popup
                            content={
                              st === EContractStatus.Cancelado
                                ? 'Não é possível alterar o status de contratos cancelados'
                                : isCarteiraListaStatusChangeBlocked(st)
                                  ? 'Use Aprovar ou Recusar CQV na modal do contrato'
                                  : 'Alterar status do contrato'
                            }
                            trigger={
                              <Dropdown
                                button
                                text="Status"
                                icon="dropdown"
                                labeled
                                className="icon status-dropdown"
                                floating
                                upward={!isFirstItem}
                                disabled={
                                  st === EContractStatus.Cancelado ||
                                  isCarteiraListaStatusChangeBlocked(st)
                                }
                                style={{
                                  ...getDefaultButtonStyle(),
                                  padding: '0.4rem 0.8rem',
                                  fontSize: '0.8rem',
                                  background: 'linear-gradient(135deg, #3498db 0%, #2980b9 100%)',
                                  color: 'white',
                                  zIndex: 10,
                                }}
                              >
                                <Dropdown.Menu
                                  style={{
                                    zIndex: 100002,
                                    maxHeight: '400px',
                                    overflowY: 'auto',
                                    overflowX: 'hidden',
                                  }}
                                  className="status-dropdown-menu"
                                >
                                  {canOnlyChangeToLimitedStatuses ? (
                                    <>
                                      <Dropdown.Header>Status Disponíveis</Dropdown.Header>
                                      <Dropdown.Item onClick={() => onStatusChange(contract.id, EContractStatus.Assinado)}>
                                        Assinado
                                      </Dropdown.Item>
                                      <Dropdown.Item onClick={() => onStatusChange(contract.id, EContractStatus.NaoAssinado)}>
                                        Não Assinado
                                      </Dropdown.Item>
                                      <Dropdown.Item onClick={() => onStatusChange(contract.id, EContractStatus.Cancelado)}>
                                        Cancelado
                                      </Dropdown.Item>
                                    </>
                                  ) : (
                                    <>
                                      <Dropdown.Header>Status Básicos</Dropdown.Header>
                                      {!shouldHideEditableStatuses && (
                                        <>
                                          <Dropdown.Item onClick={() => onStatusChange(contract.id, EContractStatus.Criado)}>
                                            Criado
                                          </Dropdown.Item>
                                          <Dropdown.Item onClick={() => onStatusChange(contract.id, EContractStatus.Assinado)}>
                                            Assinado
                                          </Dropdown.Item>
                                          <Dropdown.Item onClick={() => onStatusChange(contract.id, EContractStatus.NaoAssinado)}>
                                            Não Assinado
                                          </Dropdown.Item>
                                        </>
                                      )}
                                      {shouldHideEditableStatuses && (
                                        <Dropdown.Item disabled>
                                          <i>Status iniciais indisponíveis para este contrato</i>
                                        </Dropdown.Item>
                                      )}
                                      <Dropdown.Item onClick={() => onStatusChange(contract.id, EContractStatus.EmAnalise)}>
                                        Em Análise
                                      </Dropdown.Item>
                                      <Dropdown.Item onClick={() => onStatusChange(contract.id, EContractStatus.Ativo)}>
                                        Ativo
                                      </Dropdown.Item>
                                      <Dropdown.Item onClick={() => onStatusChange(contract.id, EContractStatus.Suspenso)}>
                                        Suspenso
                                      </Dropdown.Item>
                                      <Dropdown.Item onClick={() => onStatusChange(contract.id, EContractStatus.Cancelado)}>
                                        Cancelado
                                      </Dropdown.Item>
                                      <Dropdown.Item onClick={() => onStatusChange(contract.id, EContractStatus.Finalizado)}>
                                        Finalizado
                                      </Dropdown.Item>
                                      {hasRole('Admin') && (
                                        <>
                                          <Dropdown.Divider />
                                          <Dropdown.Header>CQV e VTME</Dropdown.Header>
                                          <Dropdown.Item
                                            onClick={() => onStatusChange(contract.id, EContractStatus.AprovadoCQV)}
                                          >
                                            Aprovado CQV
                                          </Dropdown.Item>
                                          <Dropdown.Item
                                            onClick={() => onStatusChange(contract.id, EContractStatus.RecusadoCQV)}
                                          >
                                            Recusado CQV
                                          </Dropdown.Item>
                                          <Dropdown.Item
                                            onClick={() => onStatusChange(contract.id, EContractStatus.RecusadoQualidade)}
                                          >
                                            Recusado Qualidade
                                          </Dropdown.Item>
                                          {/* RF17: 66/67/68 nunca oferecidos aqui (Seller incluso) — classificação só no modal. */}
                                          {([
                                            EContractStatus.QualidadeOk,
                                            EContractStatus.AprovadoCCV,
                                            EContractStatus.RecusadoCCV,
                                          ] as const)
                                            .filter((s) => !isOmittedFromTableStatusDropdown(s))
                                            .map((s) => (
                                              <Dropdown.Item
                                                key={s}
                                                onClick={() => onStatusChange(contract.id, s)}
                                              >
                                                {getStatusText(s)}
                                              </Dropdown.Item>
                                            ))}
                                          <Dropdown.Item
                                            onClick={() => onStatusChange(contract.id, EContractStatus.RecusadoCVP)}
                                          >
                                            Recusado CVP
                                          </Dropdown.Item>
                                          <Dropdown.Item
                                            onClick={() =>
                                              onStatusChange(contract.id, EContractStatus.TrocaDeCarteira)
                                            }
                                          >
                                            Troca de Carteira
                                          </Dropdown.Item>
                                          <Dropdown.Item
                                            onClick={() =>
                                              onStatusChange(contract.id, EContractStatus.TrocaDeCarteiraNaoAutorizada)
                                            }
                                          >
                                            Troca de Carteira Não Autorizada
                                          </Dropdown.Item>
                                          <Dropdown.Item
                                            onClick={() => onStatusChange(contract.id, EContractStatus.VtmeCriado)}
                                          >
                                            VTME Criado
                                          </Dropdown.Item>
                                        </>
                                      )}
                                    </>
                                  )}
                                  {!canOnlyChangeToLimitedStatuses && (
                                    <>
                                      <Dropdown.Divider />
                                      <Dropdown.Header>Status Intermediários</Dropdown.Header>
                                      <Dropdown.Item onClick={() => onStatusChange(contract.id, EContractStatus.CVP)}>
                                        CVP
                                      </Dropdown.Item>
                                      <Dropdown.Item
                                        onClick={() => onStatusChange(contract.id, EContractStatus.EmAnaliseBackOffice)}
                                      >
                                        Análise do BackOffice
                                      </Dropdown.Item>
                                      <Dropdown.Item
                                        onClick={() => onStatusChange(contract.id, EContractStatus.AguardandoCI)}
                                      >
                                        Aguardando CI
                                      </Dropdown.Item>
                                      <Dropdown.Item onClick={() => onStatusChange(contract.id, EContractStatus.Qualidade)}>
                                        Qualidade
                                      </Dropdown.Item>
                                      <Dropdown.Item
                                        onClick={() => onStatusChange(contract.id, EContractStatus.AguardandoFIDEL)}
                                      >
                                        Aguardando FIDEL
                                      </Dropdown.Item>
                                      {!shouldHideEditableStatuses && (
                                        <Dropdown.Item
                                          onClick={() =>
                                            onStatusChange(contract.id, EContractStatus.AguardandoCorrecaoConsultor)
                                          }
                                        >
                                          Aguardando Correção do Consultor
                                        </Dropdown.Item>
                                      )}
                                      <Dropdown.Item
                                        onClick={() =>
                                          onStatusChange(contract.id, EContractStatus.ValidandoDocumentacao)
                                        }
                                      >
                                        Validando Documentação
                                      </Dropdown.Item>
                                      <Dropdown.Item onClick={() => onStatusChange(contract.id, EContractStatus.Input)}>
                                        INPUT
                                      </Dropdown.Item>
                                      <Dropdown.Item
                                        onClick={() =>
                                          onStatusChange(contract.id, EContractStatus.AguardandoAnaliseAntiFraude)
                                        }
                                      >
                                        Aguardando Análise Anti-Fraude
                                      </Dropdown.Item>
                                      <Dropdown.Item
                                        onClick={() => onStatusChange(contract.id, EContractStatus.AnaliseSupervisor)}
                                      >
                                        Análise do Supervisor
                                      </Dropdown.Item>
                                      <Dropdown.Item
                                        onClick={() => onStatusChange(contract.id, EContractStatus.ProcedimentoNAC)}
                                      >
                                        Procedimento NAC
                                      </Dropdown.Item>
                                      <Dropdown.Item
                                        onClick={() =>
                                          onStatusChange(contract.id, EContractStatus.AguardandoDocumentacao)
                                        }
                                      >
                                        Aguardando Documentação
                                      </Dropdown.Item>
                                      <Dropdown.Item
                                        onClick={() =>
                                          onStatusChange(contract.id, EContractStatus.AguardandoEmissaoNF)
                                        }
                                      >
                                        Aguardando Emissão de NF
                                      </Dropdown.Item>
                                      <Dropdown.Item
                                        onClick={() => onStatusChange(contract.id, EContractStatus.AguardandoEntrega)}
                                      >
                                        Aguardando Entrega
                                      </Dropdown.Item>
                                      <Dropdown.Item
                                        onClick={() => onStatusChange(contract.id, EContractStatus.AreaDeAtuacao)}
                                      >
                                        Área de Atuação
                                      </Dropdown.Item>
                                      <Dropdown.Item
                                        onClick={() => onStatusChange(contract.id, EContractStatus.ReanaliseCredito)}
                                      >
                                        Reanálise de Crédito
                                      </Dropdown.Item>
                                      <Dropdown.Item
                                        onClick={() =>
                                          onStatusChange(contract.id, EContractStatus.AguardandoAnaliseGerenteVendas)
                                        }
                                      >
                                        Aguardando Análise do Gerente de Vendas
                                      </Dropdown.Item>
                                      <Dropdown.Item
                                        onClick={() =>
                                          onStatusChange(contract.id, EContractStatus.ProcedimentoTrader)
                                        }
                                      >
                                        Procedimento Trader
                                      </Dropdown.Item>
                                      <Dropdown.Item
                                        onClick={() => onStatusChange(contract.id, EContractStatus.ReanaliseADM)}
                                      >
                                        Reanálise do ADM
                                      </Dropdown.Item>
                                      <Dropdown.Item
                                        onClick={() =>
                                          onStatusChange(contract.id, EContractStatus.AguardandoProcedimentoADM)
                                        }
                                      >
                                        Aguardando Procedimento do ADM
                                      </Dropdown.Item>
                                      <Dropdown.Item
                                        onClick={() =>
                                          onStatusChange(contract.id, EContractStatus.InsucessoEntrega)
                                        }
                                      >
                                        Insucesso na Entrega
                                      </Dropdown.Item>
                                      <Dropdown.Divider />
                                      <Dropdown.Header>Status Finais</Dropdown.Header>
                                      <Dropdown.Item
                                        onClick={() =>
                                          onStatusChange(contract.id, EContractStatus.ProvisionadoAguardandoEntrega)
                                        }
                                      >
                                        Provisionado - Aguardando Entrega
                                      </Dropdown.Item>
                                      <Dropdown.Item
                                        onClick={() =>
                                          onStatusChange(contract.id, EContractStatus.ProcessoAtivacaoPortabilidades)
                                        }
                                      >
                                        Processo de Ativação de Portabilidades
                                      </Dropdown.Item>
                                      <Dropdown.Item
                                        onClick={() =>
                                          onStatusChange(contract.id, EContractStatus.ErroPortabilidade)
                                        }
                                      >
                                        Erro Portabilidade
                                      </Dropdown.Item>
                                      <Dropdown.Item
                                        onClick={() =>
                                          onStatusChange(contract.id, EContractStatus.PedidoAntigo)
                                        }
                                      >
                                        Pedido Antigo
                                      </Dropdown.Item>
                                    </>
                                  )}
                                </Dropdown.Menu>
                              </Dropdown>
                            }
                          />
                          {(hasRole('Admin') ||
                            hasRole('Validator') ||
                            hasRole('TeamLeader')) && (
                            <Dropdown
                              icon="ellipsis vertical"
                              floating
                              button
                              className="icon contract-more-actions-dropdown"
                              direction="left"
                              upward={!isFirstItem}
                              title="Mais ações"
                              style={{
                                ...getDefaultButtonStyle(),
                                padding: '0.4rem 0.55rem',
                                fontSize: '0.8rem',
                                background: 'linear-gradient(135deg, #7f8c8d 0%, #95a5a6 100%)',
                                color: 'white',
                                zIndex: 10,
                              }}
                              {...getButtonHoverEffects('default')}
                            >
                              <Dropdown.Menu
                                style={{
                                  zIndex: 100002,
                                  maxHeight: '320px',
                                  overflowY: 'auto',
                                }}
                                className="status-dropdown-menu"
                              >
                                {hasRole('Admin') && (
                                  <>
                                    <Dropdown.Item
                                      disabled={duplicatingContractId === contract.id}
                                      onClick={() => onDuplicate(contract.id)}
                                    >
                                      <Icon name="copy" />
                                      {duplicatingContractId === contract.id ? 'Duplicando…' : 'Duplicar contrato'}
                                    </Dropdown.Item>
                                    <Dropdown.Item onClick={() => onViewEditHistory(contract.id)}>
                                      <Icon name="history" />
                                      Histórico de edições
                                    </Dropdown.Item>
                                  </>
                                )}
                                {isDocusignUiEnabled() && (
                                  <Dropdown.Item
                                    disabled={st === EContractStatus.Cancelado}
                                    onClick={() => handleSendToDocusignFromMenu(contract)}
                                  >
                                    <Icon name="send" />
                                    Enviar contrato pelo Docusign
                                  </Dropdown.Item>
                                )}
                                {hasRole('Admin') &&
                                  onMarkFastchipEnviado &&
                                  contract.fastchip &&
                                  !contract.fastchipEnviadoEm && (
                                    <Dropdown.Item
                                      onClick={() => onMarkFastchipEnviado(contract.id, contract.status)}
                                    >
                                      <Icon name="shipping fast" />
                                      Marcar Fast Chip enviado
                                    </Dropdown.Item>
                                  )}
                                {hasRole('Admin') && onCancelPendingSuspensions && (
                                  <Dropdown.Item onClick={() => onCancelPendingSuspensions(contract.id, contract)}>
                                    <Icon name="cancel" />
                                    Cancelar suspensão pendente
                                  </Dropdown.Item>
                                )}
                                {(hasRole('Validator') || hasRole('Admin') || hasRole('TeamLeader')) && (
                                  <Dropdown.Item
                                    disabled={st === EContractStatus.Cancelado}
                                    onClick={() => onSendEmail(getContractIdsForVtmeEmail(contract as DisplayContract))}
                                  >
                                    <Icon name="mail" />
                                    Enviar dados por e-mail
                                  </Dropdown.Item>
                                )}
                                {(hasRole('Validator') || hasRole('Admin')) && (
                                  <Dropdown.Item onClick={() => onDelete(contract.id)}>
                                    <Icon name="trash" />
                                    Deletar contrato
                                  </Dropdown.Item>
                                )}
                              </Dropdown.Menu>
                            </Dropdown>
                          )}
                        </Button.Group>
                        )}
                      </Table.Cell>
                    </Table.Row>

                    {contract.isGroupParent && contract.groupId && expandedGroups.has(contract.groupId) && (
                      <Table.Row style={{ backgroundColor: '#f9fafb' }}>
                        <Table.Cell colSpan={showRestoreSelection ? 9 : 8} style={{ padding: '20px' }}>
                          {contract.groupContracts && contract.groupContracts.length > 0 ? (
                            <div>
                              <div
                                style={{
                                  marginBottom: '15px',
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'center',
                                }}
                              >
                                <strong style={{ fontSize: '1.1em', color: '#2c3e50' }}>
                                  <Icon name="folder open outline" />
                                  Contratos do Grupo ({contract.groupContracts.length} contratos)
                                </strong>
                                <Popup
                                  content={
                                    st === EContractStatus.Cancelado
                                      ? 'Não é possível baixar contratos cancelados'
                                      : 'Baixar Todos do Grupo (ZIP)'
                                  }
                                  trigger={
                                    <Button
                                      primary
                                      size="small"
                                      icon="download"
                                      content="Baixar Todos do Grupo (ZIP)"
                                      disabled={st === EContractStatus.Cancelado}
                                      onClick={() => onDownloadGroup(contract.groupId!)}
                                      style={getDefaultButtonStyle()}
                                      {...getButtonHoverEffects('primary')}
                                    />
                                  }
                                />
                              </div>

                              <Table compact size="small" style={{ marginTop: '10px' }}>
                                <Table.Header>
                                  <Table.Row>
                                    {showRestoreSelection && (
                                      <Table.HeaderCell collapsing textAlign="center" />
                                    )}
                                    <Table.HeaderCell width={2}>Tipo</Table.HeaderCell>
                                    <Table.HeaderCell width={2}>Total Linhas</Table.HeaderCell>
                                    <Table.HeaderCell width={3}>Valor Mensal</Table.HeaderCell>
                                    <Table.HeaderCell width={3}>Criado Em</Table.HeaderCell>
                                    <Table.HeaderCell width={2}>Status</Table.HeaderCell>
                                    <Table.HeaderCell width={4}>Ações</Table.HeaderCell>
                                  </Table.Row>
                                </Table.Header>
                                <Table.Body>
                                  {contract.groupContracts.map((subContract: ContractListDto & { administrator?: any; representatives?: any[] }) => {
                                    const subSt = normalizeContractStatus(subContract.status);
                                    const subContractTypeLabel = getContractTypeLabel(subContract);
                                    return (
                                    <Table.Row key={subContract.id}>
                                      {showRestoreSelection && (
                                        <Table.Cell collapsing textAlign="center">
                                          {renderRestoreCheckbox(subContract.id, subContract.status)}
                                        </Table.Cell>
                                      )}
                                      <Table.Cell>
                                        <Label
                                          color={
                                            (subContract.tipoContratacao || (subContract as any).contractType) === 'Aditivo'
                                              ? 'green'
                                              : 'blue'
                                          }
                                          style={{ fontWeight: 'bold' }}
                                        >
                                          <Icon
                                            name={
                                              (subContract.tipoContratacao || (subContract as any).contractType) === 'Aditivo'
                                                ? 'plus circle'
                                                : 'refresh'
                                            }
                                          />
                                          {subContract.tipoContratacao || (subContract as any).contractType}
                                        </Label>
                                      </Table.Cell>
                                      <Table.Cell>
                                        <div>
                                          <Label
                                            color="blue"
                                            size="small"
                                            style={{ cursor: 'pointer', marginBottom: '4px', display: 'inline-block' }}
                                            onClick={() => onViewLines(subContract.id, subContract)}
                                            title="Clique para ver detalhes das linhas"
                                          >
                                            <Icon name="list" />
                                            {subContract.totalLinhas || 0}{' '}
                                            {(subContract.totalLinhas || 0) === 1 ? 'linha' : 'linhas'} - Ver{' '}
                                            {(subContract.totalLinhas || 0) === 1 ? 'linha' : 'linhas'}
                                          </Label>
                                          <Popup
                                            trigger={
                                              <Icon
                                                name="info circle"
                                                color="blue"
                                                style={{ cursor: 'help', marginLeft: '5px' }}
                                              />
                                            }
                                            content={
                                              <div style={{ padding: '5px' }}>
                                                <div style={{ marginBottom: '8px' }}>
                                                  <strong>Responsável pela Auditoria:</strong>
                                                  <div style={{ marginTop: '4px', fontSize: '0.9em' }}>
                                                    {subContract.administrator?.nomeCompleto || 'Não informado'}
                                                  </div>
                                                  <div
                                                    style={{
                                                      fontSize: '0.85em',
                                                      color: '#666',
                                                      marginTop: '4px',
                                                      fontStyle: 'italic',
                                                    }}
                                                  >
                                                    (Pessoa que está na ligação com o vendedor)
                                                  </div>
                                                </div>
                                                {subContract.representatives && subContract.representatives.length > 0 && (
                                                  <div>
                                                    <strong>Representantes Legais (QSA):</strong>
                                                    <div style={{ marginTop: '4px', fontSize: '0.9em' }}>
                                                      {subContract.representatives.slice(0, 3).map((rep: any, idx: number) => (
                                                        <div key={idx} style={{ marginBottom: '2px' }}>
                                                          • {rep.nome || 'Sem nome'}
                                                        </div>
                                                      ))}
                                                      {subContract.representatives.length > 3 && (
                                                        <div
                                                          style={{
                                                            fontSize: '0.85em',
                                                            color: '#666',
                                                            fontStyle: 'italic',
                                                          }}
                                                        >
                                                          + {subContract.representatives.length - 3} mais
                                                        </div>
                                                      )}
                                                    </div>
                                                  </div>
                                                )}
                                              </div>
                                            }
                                            position="top center"
                                            size="small"
                                          />
                                        </div>
                                      </Table.Cell>
                                      <Table.Cell>
                                        <Label color="teal" size="medium">
                                          <Icon name="money bill alternate" />
                                          R${' '}
                                          {subContract.valorTotalMensal?.toFixed(2).replace('.', ',') || '0,00'}
                                        </Label>
                                      </Table.Cell>
                                      <Table.Cell>
                                        {new Date(subContract.createdAt).toLocaleDateString('pt-BR')}
                                      </Table.Cell>
                                      <Table.Cell>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                          {subSt !== null ? (
                                            <>
                                              <Label
                                                size="tiny"
                                                color={
                                                  subSt === EContractStatus.Ativo
                                                    ? 'green'
                                                    : subSt === EContractStatus.Cancelado
                                                    ? 'red'
                                                    : subSt === EContractStatus.Suspenso
                                                    ? 'orange'
                                                    : subSt === EContractStatus.Finalizado
                                                    ? 'grey'
                                                    : subSt === EContractStatus.Assinado
                                                    ? 'orange'
                                                    : subSt === EContractStatus.NaoAssinado
                                                    ? 'orange'
                                                    : subSt === EContractStatus.EmAnalise
                                                    ? 'yellow'
                                                    : subSt === EContractStatus.AprovadoCQV
                                                    ? 'green'
                                                    : subSt === EContractStatus.RecusadoCQV ||
                                                      subSt === EContractStatus.RecusadoQualidade ||
                                                      subSt === EContractStatus.RecusadoCVP ||
                                                      subSt === EContractStatus.TrocaDeCarteiraNaoAutorizada
                                                    ? 'red'
                                                    : subSt === EContractStatus.TrocaDeCarteiraSolicitada
                                                    ? 'blue'
                                                    : subSt === EContractStatus.VerificandoCarteira
                                                    ? 'teal'
                                                    : subSt === EContractStatus.TrocaDeCarteira
                                                    ? 'yellow'
                                                    : subSt === EContractStatus.VtmeCriado
                                                    ? 'blue'
                                                    : subSt === EContractStatus.PedidoAntigo
                                                    ? 'grey'
                                                    : 'teal'
                                                }
                                              >
                                                {getStatusText(subContract.status)}
                                              </Label>
                                              <StatusHistoryPopover
                                                contractId={subContract.id}
                                                currentStatus={subSt}
                                                currentStatusText={getStatusText(subContract.status)}
                                                updatedAt={subContract.updatedAt}
                                                historyTitleSuffix={` - ${subContractTypeLabel}`}
                                              />
                                              <DocusignSendHistoryPopover
                                                contractId={subContract.id}
                                                latestSend={resolveLatestDocusignSendFromContract(subContract)}
                                                historyTitleSuffix={` - ${subContractTypeLabel}`}
                                              />
                                            </>
                                          ) : (
                                            <Label size="tiny" color="grey">
                                              Criado
                                            </Label>
                                          )}
                                        </div>
                                      </Table.Cell>
                                      <Table.Cell>
                                        <Button.Group size="mini">
                                          {hasRole('Admin') && (
                                            <Popup
                                              content={
                                                isContractEditable(subContract.status)
                                                  ? 'Editar este contrato'
                                                  : 'Contrato não pode ser editado neste status'
                                              }
                                              trigger={
                                                <div style={{ display: 'inline-block' }}>
                                                  <Button
                                                    icon="edit"
                                                    title={isContractEditable(subContract.status) ? 'Editar este contrato' : 'Contrato não pode ser editado neste status'}
                                                    aria-label="Editar contrato"
                                                    disabled={!isContractEditable(subContract.status)}
                                                    onClick={() => onEdit(subContract.id)}
                                                    style={{
                                                      ...getDefaultButtonStyle(),
                                                      fontSize: '0.75rem',
                                                      padding: '0.4rem 0.6rem',
                                                      background: isContractEditable(subContract.status)
                                                        ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                                                        : '#cccccc',
                                                      color: 'white',
                                                      opacity: isContractEditable(subContract.status) ? 1 : 0.6,
                                                    }}
                                                    {...(isContractEditable(subContract.status) ? getButtonHoverEffects('default') : {})}
                                                  />
                                                </div>
                                              }
                                            />
                                          )}
                                          <Popup
                                            content={
                                              subSt === EContractStatus.Cancelado
                                                ? 'Não é possível baixar contratos cancelados'
                                                : 'Baixar PDFs deste contrato'
                                            }
                                            trigger={
                                              <Button
                                                icon="download"
                                                title="Baixar PDFs deste contrato"
                                                aria-label="Baixar PDFs"
                                                disabled={subSt === EContractStatus.Cancelado}
                                                onClick={() => onDownloadAll(subContract.id)}
                                                style={{
                                                  ...getDefaultButtonStyle(),
                                                  fontSize: '0.75rem',
                                                  padding: '0.4rem 0.6rem',
                                                }}
                                                {...getButtonHoverEffects('default')}
                                              />
                                            }
                                          />
                                          {hasRole('Admin') && (
                                            <Dropdown
                                              icon="ellipsis vertical"
                                              floating
                                              button
                                              className="icon contract-more-actions-dropdown"
                                              direction="left"
                                              title="Mais ações"
                                              style={{
                                                ...getDefaultButtonStyle(),
                                                fontSize: '0.75rem',
                                                padding: '0.35rem 0.45rem',
                                                background: 'linear-gradient(135deg, #7f8c8d 0%, #95a5a6 100%)',
                                                color: 'white',
                                              }}
                                              {...getButtonHoverEffects('default')}
                                            >
                                              <Dropdown.Menu
                                                style={{ zIndex: 100002, maxHeight: '280px', overflowY: 'auto' }}
                                                className="status-dropdown-menu"
                                              >
                                                <Dropdown.Item
                                                  disabled={duplicatingContractId === subContract.id}
                                                  onClick={() => onDuplicate(subContract.id)}
                                                >
                                                  <Icon name="copy" />
                                                  {duplicatingContractId === subContract.id
                                                    ? 'Duplicando…'
                                                    : 'Duplicar contrato'}
                                                </Dropdown.Item>
                                                {isDocusignUiEnabled() && (
                                                  <Dropdown.Item
                                                    disabled={subSt === EContractStatus.Cancelado}
                                                    onClick={() => handleSendToDocusignFromMenu(subContract)}
                                                  >
                                                    <Icon name="send" />
                                                    Enviar contrato pelo Docusign
                                                  </Dropdown.Item>
                                                )}
                                                {onMarkFastchipEnviado &&
                                                  subContract.fastchip &&
                                                  !subContract.fastchipEnviadoEm && (
                                                    <Dropdown.Item
                                                      onClick={() =>
                                                        onMarkFastchipEnviado(
                                                          subContract.id,
                                                          subContract.status
                                                        )
                                                      }
                                                    >
                                                      <Icon name="shipping fast" />
                                                      Marcar Fast Chip enviado
                                                    </Dropdown.Item>
                                                  )}
                                              </Dropdown.Menu>
                                            </Dropdown>
                                          )}
                                        </Button.Group>
                                      </Table.Cell>
                                    </Table.Row>
                                  );
                                  })}
                                </Table.Body>
                              </Table>
                            </div>
                          ) : (
                            <div style={{ textAlign: 'center', padding: '10px' }}>
                              <Loader active inline size="small" />
                              Carregando contratos do grupo...
                            </div>
                          )}
                        </Table.Cell>
                      </Table.Row>
                    )}
                  </React.Fragment>
                );
              })}
            </Table.Body>

            <Table.Footer>
              <Table.Row>
                <Table.HeaderCell colSpan="9">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      Mostrando {contracts!.items.length} de {contracts!.totalCount} contratos
                      {(filters.clientCnpj || filters.tipoContratacao || filters.status) && (
                        <span style={{ marginLeft: '10px', fontSize: '0.9em', color: '#666' }}>(filtros aplicados)</span>
                      )}
                    </div>
                    {contracts!.totalPages > 1 && (
                      <Pagination
                        activePage={currentPage}
                        totalPages={totalPages}
                        onPageChange={onPageChange}
                        size="small"
                      />
                    )}
                  </div>
                </Table.HeaderCell>
              </Table.Row>
            </Table.Footer>
          </GlassTable>
        </div>
      </div>
    </div>
    </>
  );
};
